import { useProject } from '@/features/project/_hooks/project';
import { useRecursiveOrganizationFilteredByKey } from '@/features/tenant-manager/organization/_hooks/organization';
import type { permissionType } from '@/features/tenant-manager/role/_models/role';
import { useMemo } from 'react';
import type z from 'zod';
import { useMe } from './me';

export const useTenantManagerPermission = (onlyEdit?: boolean) => {
  return useMe((data) => {
    const permission = data.role.resourceGroupPermissions.administrator;
    return permission === 'edit' || (!onlyEdit && permission === 'view');
  });
};

export const useMasterTablePermission = (onlyEdit?: boolean) => {
  return useMe((data) => {
    const permission = data.role.resourceGroupPermissions.masterTable;
    return permission === 'edit' || (!onlyEdit && permission === 'view');
  });
};

/** @deprecated useProjectPermissionAndAccessControlを使用する */
export const useProjectPermission = (onlyEdit?: boolean) => {
  return useMe((data) => {
    const permission = data.role.resourceGroupPermissions.project;
    return (
      permission === 'privilege' ||
      permission === 'edit' ||
      (!onlyEdit &&
        (permission === 'view' || permission === 'view-all-privilege'))
    );
  });
};

export const useIndicatorPermission = (onlyEdit?: boolean) => {
  return useMe((data) => {
    const permission = data.role.resourceGroupPermissions.indicator;
    return permission === 'edit' || (!onlyEdit && permission === 'view');
  });
};

export const useGlobalTagPermission = (onlyEdit?: boolean) => {
  return useMe((data) => {
    const permission = data.role.resourceGroupPermissions.globalTag;
    return permission === 'edit' || (!onlyEdit && permission === 'view');
  });
};

export const useLocalTagPermission = (onlyEdit?: boolean) => {
  return useMe((data) => {
    const permission = data.role.resourceGroupPermissions.localTag;
    return permission === 'edit' || (!onlyEdit && permission === 'view');
  });
};

/**
 * 自組織がプロジェクトに所属する組織場合にのみアクセスできるか
 * @param projectId プロジェクトID
 * @returns プロジェクトに所属する組織のみがアクセスできるか
 */
export const useProjectPermissionAndAccessControl = (
  projectId: string,
): {
  projectRole: z.infer<typeof permissionType> | undefined;
  hasAccessControlByOrganization: boolean;
  editPermission: boolean;
  viewPermission: boolean;
} => {
  const project = useProject(projectId);
  const me = useMe();

  const projectRole = me.data?.role.resourceGroupPermissions.project;

  const editPermission = !!(
    projectRole &&
    (projectRole === 'edit' || projectRole === 'privilege')
  );
  const viewPermission = !!(
    editPermission ||
    projectRole === 'view' ||
    projectRole === 'view-all-privilege'
  );

  const {
    data: organizationIdsForLoginUser,
    isPending: isOrganizationIdsForLoginUserLoading,
  } = useRecursiveOrganizationFilteredByKey({
    params: {
      ids: me.data?.organizationIds ?? [],
    },
    options: {
      reverse: true,
      filterBy: 'id',
      separator: undefined,
    },
    select: (data) => data.flat(),
  });

  const isLoading =
    project.isPending || me.isPending || isOrganizationIdsForLoginUserLoading;

  const hasPermission = useMemo<boolean>(() => {
    if (!project.data || isLoading) {
      return false;
    }

    if (!project.data.isPublished) {
      return true;
    }

    const isUserBelongsToProjectOrganizations =
      !!organizationIdsForLoginUser?.includes(project.data.organizationId);

    const isUserBelongsToProjectRelatedOrganizations =
      !!project.data.relatedOrganizationIds.some((id) =>
        organizationIdsForLoginUser?.includes(id),
      );

    const isUserBelongsToOtherResponsibleOrganizations =
      !!project.data.otherResponsibleOrganizationIds.some((id) =>
        organizationIdsForLoginUser?.includes(id),
      );

    const isUserBelongsToAdditionalJurisdictionalOrganizations =
      !!project.data.fundAdditionalAttributes?.additionalJurisdictionalUsers?.some(
        (obj) => organizationIdsForLoginUser?.includes(obj.organizationId),
      );

    return (
      projectRole === 'privilege' ||
      isUserBelongsToProjectOrganizations ||
      isUserBelongsToProjectRelatedOrganizations ||
      isUserBelongsToOtherResponsibleOrganizations ||
      isUserBelongsToAdditionalJurisdictionalOrganizations
    );
  }, [isLoading, organizationIdsForLoginUser, project.data, projectRole]);

  return {
    editPermission,
    viewPermission,
    projectRole,
    hasAccessControlByOrganization: hasPermission,
  };
};
